// Claude: BuyerSubscriptionPlanModal — Add/Edit buyer subscription plan modal 11-Apr-2026
// Claude: added minAssuredResponses, pricePerRFX, currency fields 20-Apr-2026
// Claude: closedMeetingsLimit removed 23-04-2026
// Claude: pricePerRFX → subscriptionFee; rfpResponsePendingLimit readonly system constant 25-Apr-2026
// Claude: viewOnly prop — read-only mode when buyers are associated with plan 26-04-2026
import { Box, Button, Flex, Grid, GridItem, Input, Switch, Text } from "@chakra-ui/react";
import { Modal } from "antd";
import React from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  setBuyerPlanName,
  setBuyerRfxLimit,
  setBuyerPendingMeetingsLimit,
  setBuyerMinAssuredResponses,
  setSubscriptionFee,
  setBuyerCurrency,
  setBuyerIsActive,
} from "../../../redux/slices/buyerSubscriptionSlice";

const RFX_RESPONSE_BUYER_PENDING_LIMIT = 3; // Claude: system constant — not configurable per plan 25-Apr-2026

type Props = {
  isOpen: boolean;
  onClose: () => void;
  edit: boolean;
  onSubmit: () => void;
  loading: boolean;
  viewOnly?: boolean;
};

const BuyerSubscriptionPlanModal = ({ isOpen, onClose, edit, onSubmit, loading, viewOnly }: Props) => {
  const dispatch = useDispatch();
  const { subscriptions, ...planDetails } = useSelector((state: any) => state.buyerSubscription);

  return (
    <Modal
      footer={null}
      title={viewOnly ? "View Buyer Subscription Plan" : edit ? "Edit Buyer Subscription Plan" : "Add Buyer Subscription Plan"}
      width={"30%"}
      open={isOpen}
      onCancel={onClose}
      onOk={onSubmit}
      centered
      bodyStyle={{ maxHeight: "70vh", overflowY: "auto" }}
    >
      <Box padding={2}>
        {/* Claude: field order per spec 20-Apr-2026 */}
        <Grid gap={3} templateColumns="repeat(1, 1fr)">
          <GridItem colSpan={1}>
            <Box display={"flex"} flexDirection={"row"} gap={"8px"} justifyContent={"space-between"}>
              <Text fontSize={"xs"} fontWeight={"500"}>Plan Name <span style={{ color: "red" }}>*</span></Text>
              <Input width={"50%"} height="30px" fontSize="xs" placeholder="Enter Plan Name"
                value={planDetails.planName} onChange={(e) => { dispatch(setBuyerPlanName(e.target.value)); }}
                isReadOnly={viewOnly} bg={viewOnly ? "gray.50" : undefined} />
            </Box>
          </GridItem>
          <GridItem colSpan={1}>
            <Box display={"flex"} flexDirection={"row"} gap={"8px"} justifyContent={"space-between"} alignItems={"center"}>
              <Text fontSize={"xs"} fontWeight={"500"}>Active Status <span style={{ color: "red" }}>*</span></Text>
              <Box width={"50%"}>
                <Switch size="sm" isChecked={planDetails.isActive} onChange={() => { dispatch(setBuyerIsActive(!planDetails.isActive)); }} isDisabled={viewOnly} />
              </Box>
            </Box>
          </GridItem>
          <GridItem colSpan={1}>
            <Box display={"flex"} flexDirection={"row"} gap={"8px"} justifyContent={"space-between"}>
              <Text fontSize={"xs"} fontWeight={"500"}>Currency</Text>
              <Input width={"50%"} height="30px" fontSize="xs" placeholder="e.g. USD"
                value={planDetails.currency} onChange={(e) => { dispatch(setBuyerCurrency(e.target.value)); }}
                isReadOnly={viewOnly} bg={viewOnly ? "gray.50" : undefined} />
            </Box>
          </GridItem>
          <GridItem colSpan={1}>
            <Box display={"flex"} flexDirection={"row"} gap={"8px"} justifyContent={"space-between"}>
              <Text fontSize={"xs"} fontWeight={"500"}>Subscription Fee</Text>
              <Input width={"50%"} height="30px" fontSize="xs" placeholder="e.g. 500"
                value={planDetails.subscriptionFee} onChange={(e) => { dispatch(setSubscriptionFee(e.target.value)); }}
                isReadOnly={viewOnly} bg={viewOnly ? "gray.50" : undefined} />
            </Box>
          </GridItem>
          <GridItem colSpan={1}>
            <Box display={"flex"} flexDirection={"row"} gap={"8px"} justifyContent={"space-between"}>
              <Box>
                <Text fontSize={"xs"} fontWeight={"500"}>RFX Publish Limit <span style={{ color: "red" }}>*</span></Text>
                <Text fontSize={"10px"} color={"gray.400"}>Enter -1 for unlimited</Text>
              </Box>
              <Input width={"50%"} height="30px" fontSize="xs" placeholder="e.g. 10"
                value={planDetails.rfxLimit} onChange={(e) => { dispatch(setBuyerRfxLimit(e.target.value)); }}
                isReadOnly={viewOnly} bg={viewOnly ? "gray.50" : undefined} />
            </Box>
          </GridItem>
          <GridItem colSpan={1}>
            <Box display={"flex"} flexDirection={"row"} gap={"8px"} justifyContent={"space-between"}>
              <Text fontSize={"xs"} fontWeight={"500"}>Min Assured Responses</Text>
              <Input width={"50%"} height="30px" fontSize="xs" placeholder="e.g. 5"
                value={planDetails.minAssuredResponses} onChange={(e) => { dispatch(setBuyerMinAssuredResponses(e.target.value)); }}
                isReadOnly={viewOnly} bg={viewOnly ? "gray.50" : undefined} />
            </Box>
          </GridItem>
          <GridItem colSpan={1}>
            <Box display={"flex"} flexDirection={"row"} gap={"8px"} justifyContent={"space-between"}>
              <Box>
                <Text fontSize={"xs"} fontWeight={"500"}>RFP Response Pending Limit</Text>
                <Text fontSize={"10px"} color={"gray.400"}>System constant — not configurable per plan</Text>
              </Box>
              <Input width={"50%"} height="30px" fontSize="xs" isReadOnly
                value={RFX_RESPONSE_BUYER_PENDING_LIMIT} bg="gray.50" />
            </Box>
          </GridItem>
          <GridItem colSpan={1}>
            <Box display={"flex"} flexDirection={"row"} gap={"8px"} justifyContent={"space-between"}>
              <Box>
                <Text fontSize={"xs"} fontWeight={"500"}>Pending Meetings Limit</Text>
                <Text fontSize={"10px"} color={"gray.400"}>Enter -1 for unlimited</Text>
              </Box>
              <Input width={"50%"} height="30px" fontSize="xs" placeholder="e.g. 5"
                value={planDetails.pendingMeetingsLimit} onChange={(e) => { dispatch(setBuyerPendingMeetingsLimit(e.target.value)); }}
                isReadOnly={viewOnly} bg={viewOnly ? "gray.50" : undefined} />
            </Box>
          </GridItem>
        </Grid>

        {!viewOnly && (
          <Box mt={10}>
            <Flex justifyContent={"end"}>
              <Button
                h={"40px"} background={"primary.main"} color={"white"} px={3} gap={2}
                _hover={{ background: "#2E7D32", color: "white" }}
                isLoading={loading}
                onClick={onSubmit}
              >
                Submit
              </Button>
            </Flex>
          </Box>
        )}
      </Box>
    </Modal>
  );
};

export default BuyerSubscriptionPlanModal;
